<script setup lang="ts">
import {
	ref,
	provide,
	shallowRef,
	toRefs,
	computed,
	nextTick,
	onBeforeMount,
	onBeforeUnmount,
	onMounted,
	watch,
	useTemplateRef,
} from "vue";
import dayjs from "dayjs";
import Graph from "@/modules/explorer/components/Graphs/Graph.vue";
import DocumentDO = App.Data.Explorer.DocumentDO;
import DataTableWrapper from "@/modules/dashboards/components/DataTableWrapper.vue";
import SingleMetricWrapper from "@/modules/explorer/components/SingleMetric/SingleMetricWrapper.vue";
import DashboardCardDO = App.Data.DashboardCardDO;
import {
	derivedDocumentKey,
	documentKey,
	GraphSettings,
	graphSettingsKey,
} from "@/modules/explorer/ExplorerUtils";
import { useDashboardStore } from "@/modules/dashboards/store";
import formatDateRangeForChartTitle from "@/modules/explorer/composables/formatDateRangeForChartTitle";
import { useDashboardState } from "@/modules/dashboards/components/DashboardState";
import TextOnly from "@/modules/dashboards/components/TextOnly.vue";
import ProgressCard from "@/modules/dashboards/components/ProgressCard.vue";
import useDialog from "@/ui/composables/useDialog";
import GraphImage from "@/modules/dashboards/components/GraphImage.vue";
import DownloadGraphImageMenu from "@/modules/explorer/components/downloadGraphImageMenu.vue";
import {
	dashboardGraphKey,
	useDownloadGraph,
} from "@/modules/explorer/components/Graphs/store";
import { parse } from "chartjs-graph";

import { useAbortController } from "@/modules/explorer/composables/useAbortController";
import { promptInvalidPermissions } from "@/api/helpers/loadingWrapper";
import { useApiClient } from "@/api/client";
import { ApiError, err, ok, Result } from "@/api/base";
import DownloadMenu from "@/modules/explorer/components/DownloadMenu.vue";
import ExportFileType = App.Enums.ExportFileType;
import useToast from "@/ui/composables/useToast";
const { abortController, abortRequestAndResetController } =
	useAbortController();

const { isEqual, cardsForWatcher, dashboardForDataWatcher } =
	useDashboardState();

const props = defineProps<{
	card: DashboardCardDO;
	height: number;
	rowHeight: number;
	isPdfRender: boolean;
}>();

const { card, height, isPdfRender } = toRefs(props);
const lastUpdated = ref(dayjs().unix());
const derivedDocument = shallowRef<DocumentDO>();
const titleFromComponent = ref(null);

const dashboardState = useDashboardStore().dashboardState;
const isEditMode = computed(() => dashboardState.editorMode === "edit");
const isLoading = ref(true);
const unableToFetchData = ref(false);
const isRenamingTitle = ref<boolean>(false);
const titleDraft = ref("");
const oldTitle = ref("");
const document = ref<DocumentDO>(); // can just be the prop
const graphSettings = computed<GraphSettings>(() => {
	return {
		shouldShowConfigurationText:
			card.value.should_show_graph_configuration_text,
		showOnlyOldSnapshotDate: true,
	};
});
const titleRef = useTemplateRef("titleRef");
const titleInputRef = useTemplateRef<HTMLInputElement>("titleInputRef");
const titleHeight = computed(() => {
	return titleRef.value?.offsetHeight ?? 0;
});

/**
 * The content box the body actually gets, measured rather than derived from the
 * card's own height. In print mode the contents are laid out at double size and
 * scaled down, so anything computed from the card's pixels describes a different
 * coordinate space than the one the body is laid out in — and the padding is
 * only known to the element itself.
 */
const contentRef = useTemplateRef<HTMLElement>("contentRef");
const contentHeight = ref(0);

const measureContentHeight = () => {
	const element = contentRef.value;

	if (!element) {
		return;
	}

	const style = getComputedStyle(element);

	contentHeight.value =
		element.clientHeight -
		parseFloat(style.paddingTop) -
		parseFloat(style.paddingBottom);
};

onMounted(() => {
	const observer = new ResizeObserver(() => measureContentHeight());

	if (contentRef.value) {
		observer.observe(contentRef.value);
	}

	onBeforeUnmount(() => observer.disconnect());
});

provide(derivedDocumentKey, derivedDocument);
provide(documentKey, document);
provide(graphSettingsKey, graphSettings);

// Provides the download-graph state under dashboardGraphKey so this card's own
// Graph produces the image blob and the local download menu consumes it.
useDownloadGraph(dashboardGraphKey);

const isDownloadGraphImageMenuVisible = ref(false);
const isDownloadDataTableMenuVisible = ref(false);
const openDownloadMenu = () => {
	if (card.value.type === "graph") {
		isDownloadGraphImageMenuVisible.value = true;
	} else if (card.value.type === "table") {
		isDownloadDataTableMenuVisible.value = true;
	}
};
defineExpose({ openDownloadMenu });

const downloadDataTableEmit = async (exportFileType: ExportFileType) => {
	const response = await useApiClient("dashboard").downloadDataTable(
		dashboardState.dashboard,
		card.value.uuid,
		exportFileType
	);

	if (response.isErr()) {
		useToast().error("Sorry something went wrong...", {
			title: "Export failed",
			timeout: 5000,
		});
	}
};

const emit = defineEmits(["finished-loading", "setCardHeight", "editCard"]);

const hasDocument = computed(() => {
	return !!card.value?.stored_document?.activeDocument;
});

async function deleteCard() {
	const confirm = await useDialog().confirm({
		title: "Confirm delete",
		message: `Are you sure you want to delete this card?`,
		isDelete: true,
	});

	if (!confirm) return false;

	dashboardState.dashboard.cards = dashboardState.dashboard.cards.filter(
		(c) => c.layout.i !== card.value.layout.i
	);
}

const openCardMenu = (event: MouseEvent) => {
	const button = event.currentTarget as HTMLElement;
	const rect = button.getBoundingClientRect();
	button.dispatchEvent(
		new MouseEvent("contextmenu", {
			bubbles: true,
			cancelable: true,
			clientX: rect.left,
			clientY: rect.bottom,
		})
	);
};

const isResizingCard = ref(false);
let resizeSettledTimeout: ReturnType<typeof setTimeout> | null = null;

watch(
	() => [card.value.layout.w, card.value.layout.h],
	() => {
		if (!isEditMode.value) {
			return;
		}
		isResizingCard.value = true;
		if (resizeSettledTimeout) {
			clearTimeout(resizeSettledTimeout);
		}
		resizeSettledTimeout = setTimeout(() => {
			isResizingCard.value = false;
		}, 800);
	}
);

onBeforeUnmount(() => {
	if (resizeSettledTimeout) {
		clearTimeout(resizeSettledTimeout);
	}
});

// Editor-only hint: conditional rendering is applied at export time, so the live card
// stays visible. This surfaces that a card behaves differently in exports.
const conditionalVisibilityTooltip = computed(() => {
	if (card.value.visibility_condition === "when_in_alert") {
		return "Only shown in exports while its alert is firing.";
	}
	if (card.value.visibility_condition === "when_not_in_alert") {
		return "Only shown in exports while its alert is NOT firing.";
	}
	return null;
});

const chartTitle = computed(() => {
	if (card.value.use_static_title) {
		return card.value.static_title;
	}

	if (titleFromComponent.value) {
		return titleFromComponent.value;
	}

	if (card.value.stored_document) {
		return card.value.stored_document.title;
	}

	if (
		derivedDocument.value?.period_selection ||
		document.value?.period_selection
	) {
		return formatDateRangeForChartTitle(
			derivedDocument.value?.period_selection ||
				document.value?.period_selection
		);
	}
	return "";
});

// only watching for changes that need data fetching. (excluding legendWidth for example)
watch(dashboardForDataWatcher, (v, ov) => {
	if (!isEqual(v, ov)) {
		triggerDataUpdate();
	}
});

watch(
	() => dashboardState.dashboard.legend_width,
	(legendWidth) => {
		if (derivedDocument.value) {
			derivedDocument.value = {
				...derivedDocument.value,
				legend_width: legendWidth,
			};
		}
	}
);

// Only watching this card, if you watch all cards, it would trigger a refetch
// of data for every card if one card changes.
const cardForWatcher = computed(() =>
	cardsForWatcher.value.find((c) => c.i === card.value.layout.i)
);

watch(cardForWatcher, (v, ov) => {
	if (!isEqual(v, ov)) {
		triggerDataUpdate();
	}
});

watch(
	() => dashboardState.dashboard.active_filters,
	() => {
		triggerDataUpdate();
	},
	{
		deep: true,
	}
);

const triggerDataUpdate = () => {
	if (dashboardState.editorMode === "edit") return;
	if (dayjs().unix() !== lastUpdated.value) {
		lastUpdated.value = dayjs().unix();
	}
};

watch(isLoading, (value) => {
	if (!value) {
		emit("finished-loading");
	}
});

onBeforeMount(() => {
	if (hasDocument.value) {
		document.value = card.value?.stored_document?.activeDocument ?? null;
		document.value.view_tab =
			card.value.type === "progress" ? "single-metric" : card.value.type;
	} else {
		isLoading.value = false;
	}
});

const fetchDataForGraph = async (): Promise<Result<any, ApiError>> => {
	abortRequestAndResetController();

	return await promptInvalidPermissions(
		useApiClient("dashboard").fetchDataForDocument(
			dashboardState.dashboard,
			card.value.uuid,
			abortController.value?.signal ?? null
		)
	);
};

const renameTitle = () => {
	if (!useDashboardStore().userCanEdit) {
		return;
	}

	oldTitle.value = titleDraft.value = card.value.use_static_title
		? card.value.static_title ?? ""
		: card.value.stored_document?.title ?? "";
	isRenamingTitle.value = true;
	nextTick(() => titleInputRef.value?.select());
};

const finishTitleRename = (save: boolean) => {
	if (!isRenamingTitle.value) {
		return;
	}
	isRenamingTitle.value = false;
	const newTitle = titleDraft.value.trim();
	if (save && newTitle && newTitle !== oldTitle.value) {
		card.value.static_title = newTitle;
		card.value.use_static_title = true;
		dashboardState.dashboardHasChanges = true;
	}
};

const fetchTotalsForDataTable = async (): Promise<Result<any, ApiError>> => {
	if (card.value.total_row) {
		return ok(card.value.total_row);
	} else if (isPdfRender.value) {
		return err(new ApiError(422, "Unable to fetch data"));
	}
	// abortRequestAndResetController(); This request comes right before fetchDataForDataTable, causing it to get cancelled when the abortRequestAndResetController() is called in fetchDataForDataTable().

	return await promptInvalidPermissions(
		useApiClient("dashboard").fetchTotalsForDocumentDataTable(
			dashboardState.dashboard,
			card.value.uuid,
			abortController.value?.signal ?? null
		)
	);
};

const fetchDataForDataTable = async (): Promise<Result<any, ApiError>> => {
	if (card.value.cached_data) {
		return ok(card.value.cached_data);
	} else if (isPdfRender.value) {
		return err(new ApiError(422, "Unable to fetch data"));
	}

	abortRequestAndResetController();

	return await promptInvalidPermissions(
		useApiClient("dashboard").fetchDataForDocumentDataTable(
			dashboardState.dashboard,
			card.value.uuid,
			abortController.value?.signal ?? null
		)
	);
};

const fetchDataForSingleMetric = async (): Promise<Result<any, ApiError>> => {
	if (card.value.cached_data) {
		return ok(card.value.cached_data);
	} else if (isPdfRender.value) {
		return err(new ApiError(422, "Unable to fetch data"));
	}

	abortRequestAndResetController();

	return await promptInvalidPermissions(
		useApiClient("dashboard").fetchDataForDocumentSingleMetric(
			dashboardState.dashboard,
			card.value.uuid,
			abortController.value?.signal ?? null
		)
	);
};

const fetchTargetForSingleMetric = async (): Promise<
	Result<
		{ singleMetric: App.Data.Explorer.DataTable.SingleMetricResponseDTO },
		ApiError
	>
> => {
	if (card.value.target_cached_data) {
		return ok(card.value.target_cached_data);
	} else if (isPdfRender.value) {
		return err(new ApiError(422, "Unable to fetch data"));
	}

	return await promptInvalidPermissions(
		useApiClient("dashboard").fetchDataForDocumentSingleMetric(
			dashboardState.dashboard,
			card.value.uuid,
			abortController.value?.signal ?? null,
			true
		)
	);
};

const formattedSubjectDate = computed(() => {
	if (
		card.value.use_dashboard_date_settings &&
		dashboardState.dashboard.use_single_subject_date
	) {
		return "";
	}

	if (
		!derivedDocument.value?.period_selection?.from_parsed ||
		!derivedDocument.value?.period_selection?.to_parsed
	) {
		return "";
	}

	const [from, to] = [
		parse(derivedDocument.value.period_selection.from_parsed, false),
		parse(derivedDocument.value.period_selection.to_parsed, true),
	];

	const rangeIsDays =
		from.startOf("day").isSame(from, "second") &&
		to.endOf("day").isSame(to, "second");

	const formatDate = (date: dayjs.Dayjs) => {
		let format = "DD MMM YYYY";
		if (!rangeIsDays) {
			format += " HH:mm";
		}
		return date.format(format);
	};

	const [formattedFrom, formattedTo] = [formatDate(from), formatDate(to)];
	if (formattedFrom === formattedTo) return formattedFrom;

	return `${formattedFrom} - ${formattedTo}`;
});
</script>

<template>
	<v-sheet
		v-if="hasDocument || card.type === 'text-only'"
		:class="['card', isPdfRender ? 'bg-white' : '']"
		style="position: relative"
		:data-card="card.render_as_card || dashboardState.editorMode === 'edit'"
		:data-is-render="isPdfRender"
		:data-display="card.type"
		:data-use-layout="!card.use_card_layout_height"
	>
		<v-overlay
			v-model="isLoading"
			:close-on-content-click="false"
			style="display: grid; place-items: center; border-radius: 0.5rem"
			persistent
			scrim="#cccccc"
			contained
		>
			<v-progress-circular
				close-delay="0"
				style="width: 100%"
				color="primary"
				height="6"
				indeterminate
				open-delay="0"
				rounded
			/>

			<v-label
				class="text-subtitle-1 font-weight-semibold pb-2 text-primary justify-center w-100"
			>
				Loading data
			</v-label>
		</v-overlay>

		<div
			class="toolbar"
			v-if="!isLoading && !isPdfRender && isEditMode && !isRenamingTitle"
		>
			<v-btn
				@click="openCardMenu"
				color="primary"
				v-tooltip="'Card options'"
				size="32"
				variant="text"
			>
				<i class="fa-solid fa-ellipsis-vertical"></i>
			</v-btn>
		</div>

		<DownloadGraphImageMenu
			v-if="isDownloadGraphImageMenuVisible"
			v-model="isDownloadGraphImageMenuVisible"
			:download-key="dashboardGraphKey"
			:chart-title="chartTitle"
		/>

		<DownloadMenu
			v-model="isDownloadDataTableMenuVisible"
			data-type="data-table"
			@download="downloadDataTableEmit"
		/>

		<v-fade-transition>
			<div
				v-if="isEditMode"
				class="card-dimensions"
				:data-resizing="isResizingCard"
			>
				{{ card.layout.w }}x{{ card.layout.h }}
			</div>
		</v-fade-transition>

		<div
			class="scaled-container"
			:data-scaled="dashboardState.dashboard.mode === 'print' && !isPdfRender"
			:data-title-visible="card.is_title_visible"
		>
			<!--			:data-title-visible="card.type === 'graph' && document && showChartTitle"-->
			<div
				class="scaled-content"
				:data-scaled="dashboardState.dashboard.mode === 'print' && !isPdfRender"
			>
				<div
					v-if="card.is_title_visible"
					class="card-title cursor-pointer"
					ref="titleRef"
					:data-renaming="isRenamingTitle"
					@dblclick="renameTitle"
				>
					<div
						v-if="isRenamingTitle"
						class="d-flex pa-4 ga-2 align-center"
						:class="
							card.title_align === 'center' ? 'justify-center' : undefined
						"
					>
						<input
							ref="titleInputRef"
							v-model="titleDraft"
							class="title-rename-input flex-grow-1"
							:class="`text-${card.title_align}`"
							maxlength="255"
							@mousedown.stop
							@keyup.enter="finishTitleRename(true)"
							@keyup.esc="finishTitleRename(false)"
							@blur="finishTitleRename(true)"
						/>
					</div>
					<div
						v-else-if="card.title_align === 'center'"
						class="d-flex pa-4 ga-1 justify-center align-center"
					>
						<span
							v-html="chartTitle"
							class="chart-title-wrap chart-title-text"
							style="font-weight: 500"
						/>
						<template v-if="conditionalVisibilityTooltip && !isPdfRender">
							<span
								v-tooltip="conditionalVisibilityTooltip"
								class="d-inline-flex align-center"
							>
								<v-icon size="16" class="text-medium-emphasis">
									mdi-bell-ring-outline
								</v-icon>
							</span>
							<span class="mx-1"> · </span>
						</template>
						<span
							class="chart-title-text"
							style="font-weight: 500; opacity: 0.6"
						>
							{{ formattedSubjectDate }}
						</span>
					</div>
					<div
						v-else
						class="d-flex pa-4 ga-2 justify-space-between align-center"
						:class="`text-${card.title_align}`"
					>
						<div
							v-if="card.title_align === 'left'"
							class="flex-grow-1 d-flex align-center ga-2 chart-title-wrap"
						>
							<span
								v-html="chartTitle"
								class="chart-title-text"
								style="font-weight: 500"
							/>
							<span
								v-if="conditionalVisibilityTooltip && !isPdfRender"
								v-tooltip="conditionalVisibilityTooltip"
								class="d-inline-flex align-center"
							>
								<v-icon size="16" class="text-medium-emphasis">
									mdi-bell-ring-outline
								</v-icon>
							</span>
						</div>
						<span
							class="chart-title-text"
							style="font-weight: 500; opacity: 0.6"
						>
							{{ formattedSubjectDate }}
						</span>
						<div
							v-if="card.title_align === 'right'"
							class="flex-grow-1 d-flex align-center justify-end ga-2 chart-title-wrap"
						>
							<span
								v-html="chartTitle"
								class="chart-title-text"
								style="font-weight: 500"
							/>
							<span
								v-if="conditionalVisibilityTooltip && !isPdfRender"
								v-tooltip="conditionalVisibilityTooltip"
								class="d-inline-flex align-center"
							>
								<v-icon size="16" class="text-medium-emphasis">
									mdi-bell-ring-outline
								</v-icon>
							</span>
						</div>
					</div>
					<v-divider />
				</div>

				<div
					ref="contentRef"
					class="content-container"
					:class="
						(dashboardState.dashboard.mode === 'print' && !isPdfRender) ||
						card.type === 'single-metric'
							? 'pa-3'
							: 'pa-6'
					"
				>
					<template v-if="document && !unableToFetchData">
						<graph
							v-if="card.type === 'graph' && !isPdfRender"
							@update:is-loading="(value) => (isLoading = value)"
							@update:derived-document="(value:DocumentDO) => (derivedDocument = value)"
							:fetch-data-for-graph="fetchDataForGraph"
							:download-key="dashboardGraphKey"
							:last-updated="lastUpdated"
							:is-rendering-for-gotenberg="isPdfRender"
							:is-tooltip-disabled="
								dashboardState.dashboard.mode === 'print' &&
								dashboardState.previewZoomFactor !== 2
							"
						/>
						<!--			GraphImage displays a pre-rendered image only			-->
						<GraphImage
							v-if="card.type === 'graph' && isPdfRender"
							@update:is-loading="(value) => (isLoading = value)"
							@update:derived-document="(value:DocumentDO) => (derivedDocument = value)"
							:card="card"
						/>

						<!-- Data table requires another custom dashboard wrapper because in the explorer it loads all tables and we only want to display a single.-->
						<data-table-wrapper
							v-if="card.type === 'table'"
							@update:is-loading="(value) => (isLoading = value)"
							@update:derived-document="(value:DocumentDO) => (derivedDocument = value)"
							:card="card"
							:computed-height="!card.use_card_layout_height"
							:max-height="contentHeight || height - titleHeight"
							:last-updated="lastUpdated"
							:fetch-data-for-data-table="fetchDataForDataTable"
							:fetch-totals-for-data-table="fetchTotalsForDataTable"
							@set-card-height="
								(h, headerHeight) =>
									emit('setCardHeight', h + titleHeight, headerHeight)
							"
						/>

						<single-metric-wrapper
							v-if="card.type === 'single-metric'"
							@update:is-loading="(value) => (isLoading = value)"
							@update:derived-document="(value:DocumentDO) => (derivedDocument = value)"
							:last-updated="lastUpdated"
							:fetch-data-for-single-metric="fetchDataForSingleMetric"
							:hide-title="true"
							@define-title="(value) => (titleFromComponent = value)"
						/>

						<progress-card
							v-if="card.type === 'progress'"
							:card="card"
							:last-updated="lastUpdated"
							:fetch-data-for-single-metric="fetchDataForSingleMetric"
							:fetch-target-for-single-metric="fetchTargetForSingleMetric"
							@update:derived-document="(value:DocumentDO) => (derivedDocument = value)"
							@update:is-loading="(value) => (isLoading = value)"
						/>
					</template>
					<template v-else>
						<text-only
							v-if="card.type === 'text-only'"
							:card="card"
							@update:is-loading="(value) => (isLoading = value)"
						/>
					</template>
				</div>
			</div>
		</div>
	</v-sheet>

	<v-sheet
		v-else
		class="pa-6 card"
		style="position: relative"
		:data-card="card.render_as_card || dashboardState.editorMode === 'edit'"
		:data-display="card.type"
		:data-use-layout="!card.use_card_layout_height"
	>
		<div class="toolbar" v-if="!isLoading">
			<v-btn
				@click="deleteCard"
				color="error"
				v-tooltip="'Delete this card'"
				size="32"
				variant="text"
			>
				<i class="fa-solid fa-trash"></i>
			</v-btn>
		</div>
		<div style="height: 100%">
			<div class="h-100 no-data-empty-state">
				<v-empty-state
					icon="fa: fa-regular fa-file-slash"
					text="The explorer configuration that was used for this card has been removed."
					title="Explorer configuration removed"
				></v-empty-state>
			</div>
		</div>
	</v-sheet>
</template>

<style scoped>
.card-title {
	overflow: hidden;
}

.chart-title-wrap {
	min-width: 0;
}

.chart-title-text {
	display: block;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	min-width: 0;
}

.card-title[data-renaming="true"] {
	border-color: rgb(var(--v-theme-primary));
	opacity: 1;
}

.title-rename-input {
	font: inherit;
	font-weight: 500;
	padding: 0;
	border: none;
	outline: none;
	background: transparent;
	color: inherit;
	min-width: 0;
	border: white;
}
.toolbar {
	position: absolute;
	top: 0;
	right: 0;
	padding: 0.25rem;
	background: white;
	border-bottom-left-radius: 8px;
	border-bottom: 1px solid rgba(var(--v-border-color), 0.2);
	border-left: 1px solid rgba(var(--v-border-color), 0.2);
	z-index: 1;
	box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
	opacity: 0.6;
	transition: opacity 128ms;
	cursor: default;

	&:hover {
		opacity: 1;
	}
}

.card-dimensions {
	position: absolute;
	bottom: 0;
	right: 0;
	transform: scale(0.85);
	transform-origin: bottom right;
	padding: 0.25rem 0.75rem;
	background: white;
	color: black;
	border-radius: 8px 0 0 0;
	border: 1px solid black;
	border-bottom: none;
	border-right: none;
	font-weight: 500;
	z-index: 2;
	pointer-events: none;
	opacity: 0.5;
	transition: bottom 300ms ease, right 300ms ease, transform 300ms ease,
		opacity 300ms ease, border-radius 300ms ease;
}

.card-dimensions[data-resizing="true"] {
	bottom: 50%;
	right: 50%;
	border: none;
	transform: translate(50%, 50%);
	opacity: 1;
	border-radius: 8px;
}

.card {
	height: 100%;
	overflow: hidden;
	display: block;
	place-items: unset;

	.scaled-container {
		width: 100%;
		height: 100%;

		&[data-scaled="true"] {
			width: 200% !important; /* Double the div width */
			height: 200% !important; /* Double the div height */
		}
	}

	.scaled-content {
		display: flex;
		flex-direction: column;
		width: 100%;
		height: 100%;

		&[data-scaled="true"] {
			transform: scale(0.5); /* Scale down content */
			transform-origin: top left;
			width: 100%; /* Ensures content still fits inside */
			height: 100%;
		}
	}

	.card-title {
		flex: 0 0 auto;
	}

	.content-container {
		width: 100%;
		flex: 1 1 auto;
		min-height: 0;
	}

	&[data-card="true"] {
		border: 1px solid rgba(var(--v-border-color), 0.2);
		border-radius: 8px;
		box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
	}

	&[data-is-render="true"] {
		box-shadow: unset !important;
	}

	&[data-display="graph"] {
		place-items: unset;
		display: block;
	}

	&[data-display="table"] {
		place-items: unset;

		&[data-use-layout="false"] {
			//place-items: center;
		}

		& > {
			& div {
				display: block;
			}

			& table {
				flex: 1;
			}
		}
	}
}
</style>
